import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal } from "../Motion";
import FifthDimensionModal from "../FifthDimensionModal";
import Whisper from "../Whisper";

function CurrentEntry({ dim }) {
  const navigate = useNavigate();
  return (
    <div className="relative overflow-hidden border border-white/10 bg-[#06080f] my-16 md:my-20" data-testid="dimension-01-entry">
      <div className="di-stars" />
      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center p-8 md:p-14">
        <div className="md:col-span-4">
          <Reveal y={50}>
            <div className="relative overflow-hidden aspect-square max-w-[420px]">
              <motion.img
                src={dim.artwork}
                alt="MULTIDIMENSIONAL: DIMENSION I — I AM"
                className="w-full h-full object-cover"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/15" />
            </div>
          </Reveal>
        </div>
        <div className="md:col-span-8">
          <Reveal delay={0.1}>
            <p className="font-mono text-[11px] tracking-[0.35em] text-silver mb-5">
              {dim.number} — {dim.status}
            </p>
            <h3 className="font-serif text-3xl md:text-4xl font-light tracking-[0.2em] text-white/85">
              {dim.title}
            </h3>
            <p className="di-metal font-serif text-6xl md:text-8xl font-light leading-none mt-3">
              {dim.identity || "I AM"}
            </p>
            <p className="font-mono text-xs tracking-[0.35em] text-white/45 mt-6">{dim.theme}</p>
            <p className="text-base md:text-lg leading-relaxed text-white/55 mt-7 max-w-xl">{dim.description}</p>
            <div className="flex flex-wrap items-center gap-5 mt-10">
              <button
                onClick={() => navigate("/dimension-i")}
                data-testid="dimension-01-enter"
                className="bg-[#d9dde4] text-[#05070d] font-mono text-[11px] tracking-[0.25em] px-8 py-3.5 hover:bg-white transition-colors duration-300"
              >
                ENTER DIMENSION I
              </button>
              <button
                onClick={() => navigate(`/music/${dim.releaseId}`)}
                data-testid="dimension-01-listen"
                className="border border-white/25 text-white/80 font-mono text-[11px] tracking-[0.25em] px-8 py-3.5 hover:border-silver hover:text-silver transition-colors duration-300"
              >
                LISTEN
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

function ForthcomingEntry({ dim, official }) {
  return (
    <div
      className="group relative overflow-hidden signal-veil border-t border-white/10"
      data-testid={`dimension-${dim.number}-forthcoming`}
    >
      <Whisper config={dim.whisper} id={dim.number} official={official} />
      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center py-14 md:py-20 px-2 md:px-6">
        <div className="md:col-span-2">
          <Reveal>
            <span className="font-serif text-6xl md:text-7xl font-light text-white/15 select-none">{dim.number}</span>
          </Reveal>
        </div>
        <div className="md:col-span-7">
          <Reveal delay={0.05}>
            <h3 className="font-serif text-4xl md:text-5xl font-light leading-none text-white/80">{dim.title}</h3>
            <p className="font-mono text-xs tracking-[0.35em] text-white/40 mt-4">{dim.theme}</p>
            <p className="text-sm leading-relaxed text-white/35 mt-5 max-w-md">{dim.description}</p>
          </Reveal>
        </div>
        <div className="md:col-span-3 md:text-right">
          <Reveal delay={0.1}>
            <span className="inline-block font-mono text-[10px] tracking-[0.35em] text-gold/70 border border-white/10 px-6 py-3">
              {dim.status}
            </span>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

function FifthEntry({ onOpen }) {
  return (
    <div className="border-t border-white/10" data-testid="fifth-dimension-teaser">
      <Reveal>
        <button
          onClick={onOpen}
          className="group relative w-full overflow-hidden bg-[#030303] py-24 md:py-32 block"
          data-testid="fifth-dimension-open"
        >
          <div className="signal-static" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-transparent" />
          <div className="relative flex flex-col items-center justify-center text-center px-6">
            <motion.span
              animate={{ opacity: [0.35, 0.7, 0.25, 0.6] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="font-serif text-7xl md:text-9xl text-white/25"
            >
              05
            </motion.span>
            <p className="font-mono text-xs md:text-sm tracking-[0.4em] text-red-500/90 mt-6">
              DIMENSION UNKNOWN — ACCESS RESTRICTED
            </p>
          </div>
        </button>
      </Reveal>
    </div>
  );
}

export default function Multidimensional({ dimensions, whispers = [] }) {
  const [modalOpen, setModalOpen] = useState(false);
  const current = dimensions.find((d) => d.status === "CURRENT DIMENSION");
  const forthcoming = dimensions.filter((d) => !d.restricted && d.status !== "CURRENT DIMENSION");
  const fifth = dimensions.find((d) => d.restricted);
  const officialFor = (num) =>
    whispers.filter((w) => w.dimension === num && (w.location || "dimensions") === "dimensions");

  return (
    <section id="dimensions" className="relative py-24 md:py-32 bg-[#050505]" data-testid="multidimensional">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="mb-16">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-6">THE PROJECT</p>
            <h2 className="font-serif text-5xl sm:text-7xl lg:text-9xl font-light leading-none text-white">
              MULTIDIMENSIONAL
            </h2>
            <p className="font-mono text-xs md:text-sm tracking-[0.35em] text-white/50 mt-6">
              ONE UNIVERSE. FOUR DIMENSIONS. ONE RESTRICTED SIGNAL.
            </p>
          </Reveal>
        </div>

        {current && <CurrentEntry dim={current} />}

        {forthcoming.map((dim) => (
          <ForthcomingEntry key={dim.number} dim={dim} official={officialFor(dim.number)} />
        ))}

        {fifth && <FifthEntry onOpen={() => setModalOpen(true)} />}
      </div>

      <FifthDimensionModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
}
